import React, { useEffect, useMemo, useRef } from 'react';
import { useWorldStore } from '../state/worldStore';
import { edgeInset, PHONE_IN_HAND, IS_GUEST } from '../config';
import { useStoryAccess } from '../hooks/useStoryAccess';
import { useQuestChain } from '../hooks/useQuestChain';
import { ensureMyRecords, refreshMyRecords, generatingRecords, undecidedRecords } from '../services/myRecords';
import PhoneScreen from './phone/PhoneScreen';
import { usePov, useHasBody } from '../hooks/usePov';

// The phone — treatment §3, the master interface.
//
// "The phone is how the entire existing HypeTunes app migrates into the world.
// It is not a feature; it is the container that lets us honor diegesis without
// rebuilding every screen as architecture." Design law 1 sends every function
// that has no place in the world here, and declares this **the only permitted
// non-diegetic-looking UI surface** — everything else has to be architecture.
//
// Two rules it must never break:
//   1. NEVER MODAL. The world keeps running behind it; this raises and lowers,
//      it does not pause. There is no backdrop and nothing else loses input.
//   2. NEVER TRAPS. A ringing call can always be declined — it becomes a missed
//      call you can return, not a door that closed (design law 2).
//
// The target rendering is a real in-hand 3D object with the screen as a texture
// (the Live Panels pattern). This is the DOM stand-in: same information
// architecture, same apps, so the eventual mesh swap is a renderer change rather
// than a rewrite. Being honest about that is the point — the apps below are the
// real ones and the shell is the placeholder.
//
// GATED by SERVER-SIDE story access (WORLD_STORY_USER_IDS via
// /api/dialogue/access) — admins short-circuit open, and allowlisted
// non-admin accounts get the full story: that's how a fresh test user runs
// the intro as a real player would.

const HEIGHT = 'min(600px, 70vh)';

const IS_TOUCH = typeof window !== 'undefined'
  && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);

// The phone yields to the HUD, not the other way round. Bottom-right already
// holds the audio toggle (and, on touch, the now-playing card above it) — both
// are world controls that predate this and that a player needs at any moment,
// so the phone sits ABOVE that stack rather than on top of it.
const LIFT = IS_TOUCH ? 120 : 58;

// Pocketed, the phone is a TAB, not a slid-down device.
//
// The obvious version — translate the device down and leave a peek strip — is
// wrong in a way that isn't obvious until you try to click past it: the body
// keeps extending below the strip and off the bottom of the screen, so a
// "closed" phone still covers the entire corner beneath it. Lifting the anchor
// only moved the dead zone up. A tab has no body to leave behind.

// Does the API know this scene? Asked once per key (GET /api/dialogue/caller —
// a read; 404 means the scene is not in scenes.js). Only a definite 404 says no:
// a network wobble must not silently cancel a call.
const SCENE_EXISTS = new Map();
function sceneExists(key) {
  if (!SCENE_EXISTS.has(key)) {
    SCENE_EXISTS.set(key, import('../services/api')
      .then(({ api }) => api.dialogueCaller(key))
      .then(() => true)
      .catch((err) => {
        if (/-> 404\b/.test(String(err?.message))) {
          console.warn(`[phone] not ringing: the API has no scene "${key}" (quest trigger_ref points at a scene missing from scenes.js)`);
          return false;
        }
        SCENE_EXISTS.delete(key);   // unknown — ask again next time
        return true;
      }));
  }
  return SCENE_EXISTS.get(key);
}

export default function Phone() {
  const phoneOpen = useWorldStore((s) => s.phoneOpen);
  const setPhoneOpen = useWorldStore((s) => s.setPhoneOpen);
  const togglePhone = useWorldStore((s) => s.togglePhone);
  const phoneApp = useWorldStore((s) => s.phoneApp);
  const setPhoneApp = useWorldStore((s) => s.setPhoneApp);
  const incomingCall = useWorldStore((s) => s.incomingCall);
  const ring = useWorldStore((s) => s.ring);
  const missedCalls = useWorldStore((s) => s.missedCalls);
  const phoneCall = useWorldStore((s) => s.phoneCall);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const entered = useWorldStore((s) => s.entered);
  const scene = useWorldStore((s) => s.scene);

  // Server-driven story gate (WORLD_STORY_USER_IDS) — the phone is the story's
  // front door, and a fresh non-admin test account has to be able to answer it.
  const storyOpen = useStoryAccess();
  // Guests get the phone too (Khari, 2026-08-02): the intro STARTS free — the
  // call and the ride are the pitch, the account happens at the salon.
  const guestProgress = useWorldStore((s) => s.guestProgress);
  const enabled = storyOpen && (!!myAvatarId || IS_GUEST);
  // The ways the phone can be silently absent or silent, said once, out loud.
  useEffect(() => {
    if (!entered) return;
    if (!storyOpen) console.warn('[phone] hidden — story access is closed for this account (see [story] logs above)');
    else if (!myAvatarId && !IS_GUEST) console.warn('[phone] hidden — no avatar selected. Pick who you are in AppearAs; Lyra cannot call nobody.');
    else if (IS_GUEST) {
      const gp = useWorldStore.getState().guestProgress || {};
      console.info(`[phone] guest ledger — arrivalDone: ${!!gp.arrivalDone}, rideDone: ${!!gp.rideDone}. ${gp.arrivalDone
        ? 'The call was already taken in THIS BROWSER — no ring (that is resume, not a bug). Simulate a first visit with ?newguest=1.'
        : 'Lyra rings 15s from spawn.'}`);
    }
  }, [entered, storyOpen, myAvatarId]);
  const { quests, refresh } = useQuestChain(myAvatarId, { enabled });

  // WHERE the phone lives. Third person → the HUD, because you are looking at
  // your character and the phone is yours. First person → their hand, because
  // you are looking through their eyes and the phone is theirs. No body means no
  // hand to hold it with (the earn-your-body rule), so that falls back to HUD.
  const pov = usePov();
  const hasBody = useHasBody();
  // PHONE_IN_HAND is off while the held presentation is unfinished, which sends
  // first person back to the HUD phone rather than to nothing.
  const placement = (PHONE_IN_HAND && pov === 'first' && hasBody) ? 'hand' : 'hud';

  // Published so the SCREEN can read it from either placement without each one
  // fetching its own copy.
  const setPhoneQuests = useWorldStore((s) => s.setPhoneQuests);
  const setRefreshQuests = useWorldStore((s) => s.setRefreshQuests);
  useEffect(() => { setPhoneQuests(quests); }, [quests, setPhoneQuests]);
  useEffect(() => { setRefreshQuests(refresh); }, [refresh, setRefreshQuests]);
  const rungRef = useRef(null);   // which scene we've already rung for
  const pushRecordAlert = useWorldStore((s) => s.pushRecordAlert);

  // ── Who calls, and when — decided by DATA, not by this component ────────
  // A quest declares that it reaches the player by phone (trigger_kind) and
  // which scene the call runs (trigger_ref, migration 257). So a new call ships
  // as a row: nothing here knows Lyra's name, Act 0's slug, or what she wants.
  // giver_ref 'self' = the caller is the EMBODIED AVATAR (the handoff, §4.7):
  // her name on the screen, her role "Your artist". Resolved from the store's
  // avatar roster the same way SceneDock names the session button.
  const [selfName, setSelfName] = React.useState('');
  useEffect(() => {
    if (!myAvatarId) { setSelfName(''); return undefined; }
    let dead = false;
    import('../services/api').then(({ api }) => api.myAvatars())
      .then((d) => {
        if (dead) return;
        const me = (d?.avatars || []).find((a) => String(a.avatar_id) === String(myAvatarId));
        setSelfName(me?.name || '');
      })
      .catch(() => {});
    return () => { dead = true; };
  }, [myAvatarId]);

  const pendingCall = useMemo(() => {
    // A guest has no quest rows — the arrival call is synthesized until the
    // salon signup gives them real ones. Same scene, same machinery.
    if (IS_GUEST) {
      if (guestProgress?.arrivalDone) return null;
      // A saved token mid-guest-session = the salon claim just finished and
      // the reboot is moments away. Ringing in that window replayed Act 0 at
      // the exact second the redirect hit (Khari, 2026-08-03) — stay silent
      // and let the member wake up properly.
      try { if (localStorage.getItem('world:authToken')) return null; } catch { /* private mode */ }
      return { sceneKey: 'act0_lyra_call', quest: null, from: 'Lyra', giverRef: null, role: 'Manager' };
    }
    const q = quests.find((x) => x.trigger_kind === 'phone_call' && x.trigger_ref
      && (x.status === 'available' || x.status === 'active'));
    if (!q) return null;
    const who = q.giver_ref || q.giver_kind || 'unknown';
    const isSelf = who === 'self';
    return {
      sceneKey: q.trigger_ref,
      quest: q.slug,
      from: isSelf ? (selfName || 'Her') : who.charAt(0).toUpperCase() + who.slice(1),
      // Reported when the call ends: "talk_to <giver>" is the objective shape
      // every call-delivered quest uses, so the screen never needs a per-act map.
      // The handoff's objective is 'autonomy', evaluated server-side — a null
      // giverRef just means nothing extra gets reported, which is right.
      giverRef: isSelf ? null : (q.giver_ref || null),
      role: isSelf ? 'Your artist' : q.quest_type === 'story' ? 'Manager' : 'HypeTunes',
    };
  }, [quests, selfName, guestProgress?.arrivalDone]);

  // She rings once you're actually IN the world — past the Enter gate, standing
  // on the street. Act 0 rings 15 SECONDS FROM SPAWN (Khari: 30 → 15, same
  // night — one look around, not a wait): enough to
  // a minute to just LOOK at the city before anybody wants anything. The
  // anchor is the camera SETTLING — not this effect firing, which waits on the
  // quest fetch and would silently add network time to the wait. If the quest
  // arrives after the 15s mark has already passed, she rings right away.
  // ARRIVAL, not entry. This used to latch on `entered && camSettled`, but
  // camSettled flips for any completed camera move — including the orbit ease
  // that runs while CharacterSelect is still open. So the 15s was spent picking
  // a character and she rang OVER the fly-in (Khari, 2026-08-06). `arrivedAt`
  // is stamped when the descent actually puts you down; see worldStore.
  const arrivedAt = useWorldStore((s) => s.arrivedAt);

  useEffect(() => {
    if (!enabled || !entered || !pendingCall || scene) return undefined;
    if (rungRef.current === pendingCall.sceneKey) return undefined;
    // NOBODY CALLS YOU MID-DESCENT. Until the fly-in has landed there is no
    // arrival to count from, and a phone ringing over the establishing shot
    // steps on the one moment the city gets to introduce itself. The effect
    // re-runs when arrivedAt is stamped, so this is a wait, not a miss.
    if (!arrivedAt) return undefined;
    // The handoff rings LATE on purpose (§4.7): the premiere is still playing
    // on the street, and the song gets its moment before she asks for hers.
    const sinceSpawn = Date.now() - arrivedAt;
    const delay = pendingCall.sceneKey === 'act5_handoff' ? 26000
      : pendingCall.sceneKey === 'act0_lyra_call' ? Math.max(0, 15000 - sinceSpawn)
      : 4500;
    let dead = false;
    const t = setTimeout(async () => {
      rungRef.current = pendingCall.sceneKey;
      // NEVER RING FOR A CALL THAT CANNOT BE ANSWERED. The quest row names the
      // scene (trigger_ref) but the scene itself lives in the API's scenes.js,
      // and the two can drift — act5_handoff was dropped from scenes.js on
      // 2026-08-04 while migration 278 still points the handoff call at it.
      // Answering then printed "Unknown scene: act5_handoff" on the phone, at
      // the most important moment in the intro. Better that she never rings.
      if (!(await sceneExists(pendingCall.sceneKey)) || dead) return;
      ring(pendingCall);
      setPhoneOpen(true);   // a ringing phone comes out of your pocket
    }, delay);
    return () => { dead = true; clearTimeout(t); };
  }, [enabled, entered, pendingCall?.sceneKey, scene, arrivedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // A call that ended (or was declined and returned) has to re-arm, or finishing
  // Act 0 and starting a fresh avatar would never ring again.
  useEffect(() => { if (!pendingCall) rungRef.current = null; }, [pendingCall?.sceneKey]);

  // ── Watching for the drop ────────────────────────────────────────────────
  // Once the session ends the studio isn't looking any more — the scene's poll
  // died with the conversation. The phone is the only thing still mounted while
  // you're out in the city, so it carries the watch: poll ONLY while something
  // is actually cooking, and buzz when it lands.
  const readyRef = useRef(null);   // ids already accounted for
  useEffect(() => {
    if (!enabled) return undefined;
    let dead = false;
    let timer = null;

    const sweep = async () => {
      if (dead) return;
      await refreshMyRecords();
      if (dead) return;
      const landed = undecidedRecords().filter((t) => t.status !== 'generating');
      // First sweep only establishes what was already there — otherwise every
      // old undecided take would buzz the moment you logged in.
      if (readyRef.current === null) {
        readyRef.current = new Set(landed.map((t) => t.track_id || t.id));
      } else {
        landed.forEach((t) => {
          const id = t.track_id || t.id;
          if (!readyRef.current.has(id)) {
            readyRef.current.add(id);
            pushRecordAlert(id);
          }
        });
      }
      schedule();
    };

    // No job, no polling. A studio render is minutes, so 20s is frequent enough
    // to feel immediate and rare enough to be free.
    const schedule = () => {
      if (dead) return;
      const cooking = generatingRecords().length > 0;
      timer = setTimeout(sweep, cooking ? 20000 : 120000);
    };

    ensureMyRecords(myAvatarId).then(sweep);
    return () => { dead = true; clearTimeout(timer); };
  }, [enabled, myAvatarId]); // eslint-disable-line react-hooks/exhaustive-deps

  // P raises and lowers the phone — but not while the player is typing, or every
  // "p" in a message to a producer would pocket it mid-sentence.
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (e.key !== 'p' && e.key !== 'P') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      const tag = el?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) return;
      togglePhone();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, togglePhone]);

  if (!enabled) return null;

  const badge = (incomingCall ? 1 : 0) + missedCalls.length;

  return (
    <>
      <style>{`
        @keyframes ht-ring { 0%,100% { transform: rotate(0) } 0.5%,3.5% { transform: rotate(-9deg) } 2%,5% { transform: rotate(9deg) } 6% { transform: rotate(0) } }
        @keyframes ht-pulse { 0% { box-shadow: 0 0 0 0 rgba(125,255,157,0.45) } 70% { box-shadow: 0 0 0 18px rgba(125,255,157,0) } 100% { box-shadow: 0 0 0 0 rgba(125,255,157,0) } }
        @media (prefers-reduced-motion: reduce) {
          .ht-phone, .ht-ringing, .ht-answer { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* Pocketed. Kept as its own element so the device below can stay MOUNTED
          while hidden — pocketing the phone mid-call must not destroy the call. */}
      {placement === 'hud' && !phoneOpen && (
        <button
          onClick={togglePhone}
          style={{
            position: 'fixed', right: edgeInset(14, 'right'),
            bottom: `calc(env(safe-area-inset-bottom) + ${LIFT}px)`, zIndex: 74,
            display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
            padding: '9px 14px', borderRadius: 999, pointerEvents: 'auto',
            background: 'rgba(14,9,6,0.9)', border: '1px solid rgba(255,178,94,0.45)',
            color: '#ffe9c4', font: '700 13px system-ui',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
            animation: incomingCall ? 'ht-ring 1.6s ease-in-out infinite' : 'none',
          }}
          aria-label="Take out the phone"
        >
          <span style={{ fontSize: 15, lineHeight: 1 }}>📱</span>
          {badge > 0 && (
            <span style={{
              background: '#ff5e78', color: '#150a0c', borderRadius: 999,
              padding: '1px 6px', font: '800 10px system-ui',
            }}>{badge}</span>
          )}
        </button>
      )}

      {/* THIRD PERSON: the phone is yours, so it sits in the HUD. */}
      {placement === 'hud' && (
        <div style={hudFrame(phoneOpen)}><PhoneScreen onToggle={togglePhone} /></div>
      )}
    </>
  );
}

// The HUD frame: fixed bottom-right, above the audio toggle, animating open.
const hudFrame = (open) => ({
  position: 'fixed', right: edgeInset(14, 'right'),
  bottom: `calc(env(safe-area-inset-bottom) + ${LIFT}px)`,
  height: HEIGHT, aspectRatio: '9 / 18.5', zIndex: 74,
  transform: open ? 'translateY(0) scale(1)' : 'translateY(22px) scale(0.97)',
  opacity: open ? 1 : 0,
  transition: 'transform 300ms cubic-bezier(.2,.9,.25,1), opacity 200ms ease',
  // Closed, it must be inert as well as invisible — an opaque-but-faded device
  // would still eat every click in the corner it covers.
  pointerEvents: open ? 'auto' : 'none',
});
